import { DatePipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Client } from '../../../api/app-types/client.app';
import {
	ContactPointCategory,
	ContactPointKind,
	isEmailContactPointCategory,
	isFaxContactPointCategory,
	isPhoneContactPointCategory,
	isSmsContactPointCategory,
	isWebContactPointCategory,
	resolveContactPointKind,
} from '../../../api/app-types/contact-point.app';
import { County } from '../../../api/app-types/county.app';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { LvrPermissionDirective } from '../../../shared/permissions/permission.directive';

@Component({
	selector: 'lvr-client-details-view',
	imports: [TranslocoModule, MatIconModule, DatePipe, LvrPaddingDirective, LvrPermissionDirective],
	templateUrl: './client-details-view.html',
	styleUrl: './client-details-view.scss',
})
export class ClientDetailsView {
	public readonly client = input<Client | undefined>();
	public readonly counties = input<County[]>([]);

	private readonly route = inject(ActivatedRoute);
	private readonly transloco = inject(TranslocoService);
	private readonly data = toSignal(this.route.data);

	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;

	public readonly clientCounty = computed(() => {
		const client = this.client();

		if (!client?.residenceDistrict) {
			return undefined;
		}

		return this.counties().find((county) => county.id === client.residenceDistrict);
	});

	public readonly contactPointTypes = computed(
		() => (this.data()?.['contactPointTypes'] as PaginatedResponse<ContactPointCategory>)?.entities ?? [],
	);

	/**
	 * Provides quick access to category information via
	 * categoryContactPointId.
	 */
	private readonly contactPointTypesById = computed<ReadonlyMap<number, ContactPointCategory>>(() => {
		return new Map(this.contactPointTypes().map((contactPointType) => [contactPointType.id, contactPointType]));
	});

	public readonly preferredCommunicationWay = computed(() => {
		return this.client()?.contactPoints?.find((contactPoint) => contactPoint.preferredWayOfContact);
	});

	public readonly communicationWays = computed(() => {
		const contactPoints = this.client()?.contactPoints ?? [];
		const preferredCommunicationWay = this.preferredCommunicationWay();

		return contactPoints.filter((contactPoint) => contactPoint !== preferredCommunicationWay);
	});

	/**
	 * Checks if the given date string is valid and different from
	 * DateOnly.MinValue ('0001-01-01').
	 */
	public isValidDate(input?: Date | string): boolean {
		if (!input) {
			return false;
		}

		const date = new Date(input);

		if (Number.isNaN(date.getTime())) {
			return false;
		}

		const isMinDate = date.getFullYear() === 1 && date.getMonth() === 0 && date.getDate() === 1;

		return !isMinDate;
	}

	private getContactPointTypeCategory(categoryContactPointId?: number): ContactPointCategory | undefined {
		if (categoryContactPointId == null) {
			return undefined;
		}

		return this.contactPointTypesById().get(categoryContactPointId);
	}

	public getContactPointKind(categoryContactPointId?: number): ContactPointKind {
		return resolveContactPointKind(categoryContactPointId);
	}

	public isPhoneType(categoryContactPointId?: number): boolean {
		return isPhoneContactPointCategory(categoryContactPointId);
	}

	public isEmailType(categoryContactPointId?: number): boolean {
		return isEmailContactPointCategory(categoryContactPointId);
	}

	public isSmsType(categoryContactPointId?: number): boolean {
		return isSmsContactPointCategory(categoryContactPointId);
	}

	public isFaxType(categoryContactPointId?: number): boolean {
		return isFaxContactPointCategory(categoryContactPointId);
	}

	public isWebType(categoryContactPointId?: number): boolean {
		return isWebContactPointCategory(categoryContactPointId);
	}

	private getCommunicationTypeLabelKey(categoryContactPointId?: number): string {
		const kind = this.getContactPointKind(categoryContactPointId);

		return `institution.details.communication_type.${kind}`;
	}

	public getCommunicationTypeLabel(categoryContactPointId?: number): string {
		const categoryRemark = this.getContactPointTypeCategory(categoryContactPointId)?.remark?.trim();

		if (categoryRemark) {
			return categoryRemark;
		}

		return this.transloco.translate(this.getCommunicationTypeLabelKey(categoryContactPointId));
	}

	public shouldShowRemark(remark?: string, categoryContactPointId?: number): boolean {
		const normalizedRemark = remark?.trim().toLowerCase();

		if (!normalizedRemark) {
			return false;
		}

		const categoryRemark = this.getContactPointTypeCategory(categoryContactPointId)?.remark?.trim().toLowerCase();

		if (!categoryRemark) {
			return true;
		}

		return normalizedRemark !== categoryRemark;
	}
}
